import React, { useContext, useState } from "react";
import styled from "styled-components";
import ActionHeader from "./header";
import { draftStore, sponsorsStore } from "../../../store";
import { Row, Col, Container, Wrapper } from "../../../components/layout";
import { OutlineButton, PrimaryButton } from "../../../components/buttons";
import TeamsCheckIcon from "../../../assets/icons/teams-check.svg";
import { SvgIconContainer } from "../../../components/icons";
import {
  FcRank,
  PlayerLastName,
  PlayerFirstName,
} from "../../../components/text";
import { useParams, useHistory } from "react-router-dom";
import useSaveGuestOrGetItem from "../../../lib/hooks/useSaveOrGetItem";
import LoadingIndicator from "../../../components/loading";
import { ExitDraftModal, SaveItemModal } from "../../../components/modal";
import { mobileBreakpoint, parseHeight } from "../../../lib";
import { updateItemToWP } from "../../../actions/api";
import useGetUserData from "../../../lib/hooks/useGetUserData";
import { PresentedBy, SocialMediaList, PlayerFlag } from "../../../components";
import { useMediaQuery } from "react-responsive";
import { PlayerCard } from "../runningDraft/draftSummary";

const ShareText = styled.h3`
  font-weight: 400;
  margin-right: 28px;
  @media (max-width: 900px) {
    visibility: hidden;
    display: none;
  }
`;

const TeamsCount = styled.p`
  font-size: 14px;
  text-transform: uppercase;
  font-weight: 700;
`;

const RoundButton = styled(OutlineButton)`
  margin-right: ${(props) => (props.margin ? props.margin : "18px")};
`;

const TeamLogo = styled.img.attrs((props) => ({
  src: props.src,
}))`
  width: 44px;
  height: 44px;
`;

const Td = styled.div`
  width: ${(props) => props.width};
  font-size: 14px;
  line-height: 16px;
  text-align: ${(props) => (props.textAlign ? props.textAlign : "left")};
`;

const PlayerCardContainer = styled(Row)`
  background-color: #161718;
  margin-bottom: 5px;
  min-height: 62px;
  align-items: center;
  flex-wrap: nowrap;
  position: relative;
`;

const PickNumber = styled.span`
  font-size: 18px;
  font-weight: 700;
`;

const ResultsWrapper = styled(Wrapper)`
  max-width: 920px;
  margin-top: 0;
  padding-top: 0px;
  @media (max-width: 900px) {
    padding: 0;
  }
`;
const HeaderWrapper = styled(Wrapper)`
  margin-top: 0;
  padding-top: 0px;
`;

const SocialMediaRow = styled.div`
  display: flex;
  align-items: center;
`;

const TeamsCheckIconContainer = styled.img.attrs((props) => ({
  src: props.src,
}))`
  margin-right: 5px;
`;

function getFilteredPlayers(players, roundFilter, showPlayersFilter) {
  const filteredPlayers = players
    .filter(
      (player) => roundFilter === "ALL" || player.roundPicked === roundFilter
    )
    .filter((player) =>
      showPlayersFilter === "MINE" ? player.pickedBy.isUserTeam : true
    );
  return filteredPlayers;
}

const PlayerRow = ({ player, isMobile, rankingTitle }) =>
  isMobile ? (
    <PlayerCard player={player} />
  ) : (
    <PlayerCardContainer>
      <SvgIconContainer show={player.pickedBy.isUserTeam}>
        <svg width="31px" height="31px" viewBox="0 0 31 31" version="1.1">
          <path
            d="M0 0L31 0L0 31L0 0Z"
            id="Rectangle"
            fill="#D6001C"
            stroke="none"
          />
          <path
            d="M0 3.09627L2.60266 6L8 0"
            transform="translate(6 6)"
            id="Path-2"
            fill="none"
            stroke="#FFFFFF"
          />
        </svg>
      </SvgIconContainer>
      <Td width="10%" flexDirection="column">
        <Row justifyContent="center">
          <PickNumber>{player.numberPicked}</PickNumber>
        </Row>
        <Row justifyContent="center">
          <FcRank>
            {rankingTitle ?? "RANK"} {player.fcRank}
          </FcRank>
        </Row>
      </Td>
      <Td width="7%">
        <TeamLogo src={player.pickedBy.logo} />
      </Td>
      <Td width="22%">
        <PlayerLastName>{player.lastName}</PlayerLastName>{" "}
        <PlayerFirstName>{player.firstName}</PlayerFirstName>
      </Td>
      <Td width="5%">{player.position}</Td>
      <Td width="20%">{player.team}</Td>
      <Td width="15%">{player.league}</Td>
      <Td width="8%">{parseHeight(player.height)}</Td>
      <Td width="8%">{player.weight} lbs</Td>
      <Td width="6%">
        <PlayerFlag
          player={player}
          width="24px"
          height="13px"
          marginLeft="0px"
        />
      </Td>
    </PlayerCardContainer>
  );

async function saveDraftToAccount(
  data,
  title,
  postId,
  userId,
  history,
  setSubmitting
) {
  setSubmitting(true);
  await updateItemToWP(data, "drafts", title, postId, userId);
  setSubmitting(false);
  history.push("/mock-drafts");
}

function DraftResults() {
  let history = useHistory();
  const isMobile = useMediaQuery({ maxWidth: mobileBreakpoint });
  const { state } = useContext(draftStore);
  const {
    state: { mockDraft },
  } = useContext(sponsorsStore);
  const [submitting, setSubmitting] = useState(false);
  const [isExitModalOpen, setExitModalOpen] = useState(false);
  const [isSaveModalOpen, setSaveModalOpen] = useState(false);
  const [title, setTitle] = useState("");
  const { draftId } = useParams();
  const {
    status: { selectedPlayers },
    options: { totalRounds, selectedTeams, year, rankingTitle },
  } = state;
  const { userId, username, userCanSave } = useGetUserData();
  const [data, loading] = useSaveGuestOrGetItem(
    draftId,
    { selectedPlayers, selectedTeams, totalRounds, year },
    "drafts"
  );
  const [roundFilter, setRoundFilter] = useState("ALL");
  const [showPlayersFilter, setShowPlayersFilter] = useState("ALL");
  const filteredPlayers = getFilteredPlayers(
    data.selectedPlayers,
    roundFilter,
    showPlayersFilter
  );

  // console.log(data, loading, "draftId")
  return (
    <HeaderWrapper>
      <ExitDraftModal
        isOpen={isExitModalOpen}
        handleModalOpen={setExitModalOpen}
        handleSaveModalOpen={setSaveModalOpen}
        userCanSave={userCanSave}
        isResults
      />
      <SaveItemModal
        isOpen={isSaveModalOpen}
        handleModalOpen={setSaveModalOpen}
        item="draft"
        userRole=""
        title={title}
        setTitle={setTitle}
        submitting={submitting}
        saveItem={() =>
          saveDraftToAccount(
            { selectedPlayers, selectedTeams, totalRounds, year, username },
            title,
            data.wpID,
            userId,
            history,
            setSubmitting
          )
        }
      />
      {!draftId && (
        <ActionHeader
          heading="Mock Draft"
          subHeading={
            <SocialMediaRow>
              <ShareText>Share results</ShareText>
              <SocialMediaList postId={data.wpID} type="drafts" />
            </SocialMediaRow>
          }
          userId={userId}
          openSaveModal={() => setSaveModalOpen(true)}
          handleOpenExitModal={() => setExitModalOpen(true)}
          userCanSave={userCanSave}
          sponsorLogo={mockDraft.url}
        />
      )}
      {loading ? (
        <LoadingIndicator />
      ) : (
        <ResultsWrapper>
          {draftId && (
            <Row
              alignItems="center"
              justifyContent="space-between"
              margin="30px 0 10px 0"
            >
              <Col>
                <PrimaryButton onClick={() => history.push("/mock-draft")}>
                  Start a mock draft
                </PrimaryButton>
              </Col>
              <Col alignItems="center">
                <PresentedBy logo={mockDraft.url} />
              </Col>
            </Row>
          )}
          <Row
            alignItems="center"
            justifyContent="space-between"
            margin="0 0 10px 0"
          >
            <Col>
              <h1>
                {`${data.username ? data.username + "'s" : ""}`} Draft Results
              </h1>
            </Col>
            <Col alignItems="center">
              <TeamsCheckIconContainer src={TeamsCheckIcon} />
              <TeamsCount>{`${data.selectedTeams.length} Team${
                data.selectedTeams.length > 1 ? "s" : ""
              }`}</TeamsCount>
            </Col>
          </Row>
          <Row justifyContent="space-between" margin="0 0 10px 0">
            <Col>
              <Col>
                <RoundButton
                  selected={roundFilter === "ALL"}
                  onClick={() => setRoundFilter("ALL")}
                >
                  All
                </RoundButton>
              </Col>
              {Array(data.totalRounds)
                .fill()
                .map((val, id) => (
                  <Col key={id}>
                    <RoundButton
                      selected={roundFilter === id + 1}
                      onClick={() => setRoundFilter(id + 1)}
                    >
                      Rnd {id + 1}
                    </RoundButton>
                  </Col>
                ))}
            </Col>
            <Col>
              <RoundButton
                selected={showPlayersFilter === "ALL"}
                onClick={() => setShowPlayersFilter("ALL")}
              >
                All Picks
              </RoundButton>
              <RoundButton
                margin="0px"
                selected={showPlayersFilter === "MINE"}
                onClick={() => setShowPlayersFilter("MINE")}
              >
                My Picks
              </RoundButton>
            </Col>
          </Row>
          <Container>
            {filteredPlayers.map((player, id) => (
              <PlayerRow
                key={player.id}
                player={player}
                isMobile={isMobile}
                rankingTitle={rankingTitle}
              />
            ))}
          </Container>
        </ResultsWrapper>
      )}
    </HeaderWrapper>
  );
}

export default DraftResults;
